import { Controller } from '@hotwired/stimulus';
import { subscribeTo } from '../channels/analytics_team_channel';

export default class extends Controller {
  static targets = ['feed', 'input'];
  static values = { teamId: Number, sessionId: Number };

  connect() {
    if (this.teamIdValue) {
      subscribeTo(this.teamIdValue);
    }

    if (this.hasFeedTarget) {
      this.feedTarget.scrollTop = this.feedTarget.scrollHeight;
    }

    // Listen for broadcasts
    this._statusHandler = (event) => this.handleTeamStatus(event.detail);
    this._reportHandler = (event) => this.handleReportReady(event.detail);
    document.addEventListener('analytics-team:status', this._statusHandler);
    document.addEventListener('analytics-team:report', this._reportHandler);

    // SQL toggle delegation
    this._sqlToggleHandler = (event) => {
      const toggle = event.target.closest('.activity-sql-toggle');
      if (!toggle) return;
      const item = toggle.closest('.activity-item');
      if (!item) return;
      const detail = item.querySelector('.activity-sql-detail');
      if (!detail) return;
      const isHidden = detail.style.display === 'none';
      detail.style.display = isHidden ? 'block' : 'none';
      toggle.textContent = isHidden ? 'Hide SQL' : 'SQL';
    };
    document.addEventListener('click', this._sqlToggleHandler);
  }

  disconnect() {
    if (this._statusHandler) document.removeEventListener('analytics-team:status', this._statusHandler);
    if (this._reportHandler) document.removeEventListener('analytics-team:report', this._reportHandler);
    if (this._sqlToggleHandler) document.removeEventListener('click', this._sqlToggleHandler);
  }

  // ── Status Handling ───────────────────────────────────

  handleTeamStatus(data) {
    if (data.status === 'working') {
      this.setWorking(true);
    } else if (data.status === 'done') {
      // No reload — reports stream in as cards via report_ready broadcasts,
      // and a reload here would wipe an expanded card the owner is reading
      // or a half-typed chat message.
      this.setWorking(false);
      document.querySelector('.analytics-report-generating-banner')?.remove();
    }
  }

  // A report finished — prepend its card to the conveyor live. With
  // bite-size cycles delivering up to 3 reports, reloading per report
  // (the old behavior) yanked the page out from under the owner.
  handleReportReady(data) {
    // Ignore reports for a conversation other than the one on screen
    if (data.session_id && this.sessionIdValue && data.session_id !== this.sessionIdValue) return;

    const list = document.querySelector('.analytics-report-list');
    if (!list || !data.html) {
      // Empty state (first report of the session) — reload once to build the list
      window.location.reload();
      return;
    }

    // generate_report can re-render an existing report (QA correction flow) —
    // replace its card in place rather than duplicating it
    const existing = list.querySelector(`[data-report-id="${data.report_id}"]`);
    if (existing) {
      existing.outerHTML = data.html;
    } else {
      list.insertAdjacentHTML('afterbegin', data.html);
      const count = document.querySelector('.analytics-report-count');
      if (count) count.textContent = list.children.length;
    }
  }

  setWorking(working) {
    // Update button state
    const btn = document.getElementById('generate-reports-btn');
    if (btn) {
      if (working) {
        btn.disabled = true;
        btn.dataset.originalText = btn.textContent;
        btn.innerHTML = '<span class="working-dots"><span></span><span></span><span></span></span> Working...';
      } else {
        btn.disabled = false;
        btn.textContent = btn.dataset.originalText || 'Generate Report';
      }
    }

    // Show/hide working indicator in feed
    if (working) {
      this.showWorkingIndicator();
    } else {
      this.hideWorkingIndicator();
    }
  }

  // ── Chat ──────────────────────────────────────────────

  toggleSessionList(event) {
    event.preventDefault();
    const list = document.getElementById('analytics-session-list');
    if (!list) return;
    const isHidden = list.style.display === 'none';
    list.style.display = isHidden ? 'block' : 'none';

    if (isHidden) {
      const close = (e) => {
        if (!list.contains(e.target) && !event.currentTarget.contains(e.target)) {
          list.style.display = 'none';
          document.removeEventListener('click', close);
        }
      };
      setTimeout(() => document.addEventListener('click', close), 0);
    }
  }

  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendChat();
    }
  }

  sendChat() {
    if (!this.hasInputTarget) return;
    const message = this.inputTarget.value.trim();
    if (!message) return;

    this.inputTarget.value = '';
    this.setWorking(true);

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    fetch('/analytics_team/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ message })
    })
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        this.setWorking(false);
        console.error('Chat error:', data.error);
      }
    })
    .catch(error => {
      this.setWorking(false);
      console.error('Chat error:', error);
    });
  }

  // ── Generate Reports ──────────────────────────────────

  generateReports(event) {
    event.preventDefault();
    this.setWorking(true);

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    fetch('/analytics_team/generate_reports', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        this.setWorking(false);
        console.error('Generate reports error:', data.error);
      }
    })
    .catch(error => {
      this.setWorking(false);
      console.error('Generate reports error:', error);
    });

    return false;
  }

  // ── Report Actions ────────────────────────────────────

  refreshFeed(event) {
    event.preventDefault();
    window.location.reload();
  }

  // Stop a card-action click from bubbling up to the card's expand handler.
  stopPropagation(event) {
    event.stopPropagation();
  }

  // Card header click — expand/collapse inline. Iframe src is set lazily
  // on first open so a session with 10 reports doesn't fetch 10 iframes
  // until the owner actually wants to see them.
  toggleReportCard(event) {
    const card = event.currentTarget.closest('.analytics-report-card');
    if (!card) return;

    const body = card.querySelector('.analytics-report-card-body');
    const iframe = card.querySelector('.analytics-report-card-iframe');
    const caret = card.querySelector('.analytics-report-card-caret');
    if (!body || !iframe) return;

    const isOpen = !body.hidden;
    if (isOpen) {
      body.hidden = true;
      card.classList.remove('expanded');
      if (caret) caret.textContent = '▸'; // ▸
    } else {
      if (!iframe.src && iframe.dataset.src) iframe.src = iframe.dataset.src;
      body.hidden = false;
      card.classList.add('expanded');
      if (caret) caret.textContent = '▾'; // ▾
    }
  }

  // "Open full" action — open the report at native 900px in the fullscreen modal.
  expandReport(event) {
    event.preventDefault();
    event.stopPropagation();
    const card = event.currentTarget.closest('[data-report-url]');
    const reportUrl = card?.dataset?.reportUrl;
    if (!reportUrl) return;

    const modal = document.getElementById('report-fullscreen-modal');
    const fullscreenIframe = document.getElementById('report-fullscreen-iframe');
    const titleEl = document.getElementById('report-fullscreen-title');
    if (modal && fullscreenIframe) {
      fullscreenIframe.src = reportUrl;
      if (titleEl) titleEl.textContent = card.dataset.reportTitle || 'Analytics Report';
      modal.dataset.currentReportUrl = reportUrl;
      modal.dataset.currentReportTitle = card.dataset.reportTitle || 'analytics-report';
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
    }
  }

  closeFullscreen(event) {
    event.preventDefault();
    const modal = document.getElementById('report-fullscreen-modal');
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    }
  }

  downloadReport(event) {
    event.preventDefault();
    event.stopPropagation();
    // Prefer the URL on the clicked link; fall back to the open fullscreen modal.
    const link = event.currentTarget;
    const modal = document.getElementById('report-fullscreen-modal');
    const reportUrl = link?.dataset?.reportUrl || modal?.dataset?.currentReportUrl;
    const reportTitle = link?.dataset?.reportTitle || modal?.dataset?.currentReportTitle || 'analytics-report';
    if (!reportUrl) return;

    fetch(reportUrl)
      .then(r => r.text())
      .then(html => {
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const slug = reportTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'analytics-report';
        a.download = `${slug}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
  }

  // ── Working Indicator ─────────────────────────────────

  showWorkingIndicator() {
    const feed = document.getElementById('analytics-team-feed');
    if (!feed || document.getElementById('analytics-team-working')) return;

    const html = `
      <div class="activity-item activity-working" id="analytics-team-working">
        <div class="activity-content">
          <span>Team is working</span>
          <span class="working-dots"><span></span><span></span><span></span></span>
        </div>
      </div>
    `;
    feed.insertAdjacentHTML('beforeend', html);
    feed.scrollTop = feed.scrollHeight;
  }

  hideWorkingIndicator() {
    const indicator = document.getElementById('analytics-team-working');
    if (indicator) indicator.remove();
  }
}
